import React from 'react';
import { Flex, Spin, Typography, theme } from 'antd';

import { replaceChartsWithImages } from '~library/chartjs';

type PrintableExportProps = React.PropsWithChildren<{
  subject: string;
  ready: boolean;
  chartIds?: string[];
}>;

type ExportStage = 'preparing' | 'printing';

export function PrintableExport({ subject, ready, chartIds, children }: PrintableExportProps) {
  const [stage, setStage] = React.useState<ExportStage>('preparing');
  const hasStartedPrinting = React.useRef(false);

  React.useEffect(() => {
    if (!ready || hasStartedPrinting.current) return;
    hasStartedPrinting.current = true;

    const startPrinting = () => setStage('printing');
    if (chartIds) {
      replaceChartsWithImages(chartIds, startPrinting);
    } else {
      startPrinting();
    }
  }, [ready, chartIds]);

  // `window.print()` blocks, so calling it in the same tick as the dismissal leaves the overlay frozen on screen behind the dialog.
  React.useEffect(() => {
    if (stage !== 'printing') return;
    return afterNextPaint(() => window.print());
  }, [stage]);

  return (
    <>
      {stage === 'preparing' && <ExportLoadingOverlay subject={subject} />}
      {children}
    </>
  );
}

function afterNextPaint(callback: () => void): () => void {
  let timeoutId: number | undefined;
  const frameId = requestAnimationFrame(() => {
    timeoutId = window.setTimeout(callback);
  });

  return () => {
    cancelAnimationFrame(frameId);
    window.clearTimeout(timeoutId);
  };
}

function ExportLoadingOverlay({ subject }: { subject: string }) {
  const { token } = theme.useToken();

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap="large"
      className="dashboard-export-loading-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        padding: token.paddingLG,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: token.colorBgLayout,
          opacity: 0.5,
        }}
      />
      <Flex
        vertical
        align="center"
        justify="center"
        gap="large"
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 480,
          padding: token.paddingLG,
          background: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
        }}
      >
        <Spin size="large" />
        <Typography.Title level={4} style={{ margin: 0, textAlign: 'center' }}>
          Preparing your export
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ textAlign: 'center', margin: 0 }}>
          Gathering {subject} data and charts. The print dialog will open on its own when everything is ready. Please
          keep this tab open.
        </Typography.Paragraph>
      </Flex>
    </Flex>
  );
}
